import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { EngineerDto, OverworkSignalsDto, OverworkOverrideDto, BaselineHistoryEntryDto } from '../types/api';

export const engineerKeys = {
  all:             ()          => ['engineers']                             as const,
  detail:          (id: string) => ['engineers', id]                        as const,
  signals:         (id: string) => ['engineers', id, 'signals']             as const,
  overrides:       (id: string) => ['engineers', id, 'overrides']           as const,
  baselineHistory: (id: string) => ['engineers', id, 'baseline-history']   as const,
};

export function useEngineerList() {
  return useQuery({
    queryKey: engineerKeys.all(),
    queryFn: () => client.get<EngineerDto[]>('/engineers').then((r) => r.data),
  });
}

export function useEngineer(id: string) {
  return useQuery({
    queryKey: engineerKeys.detail(id),
    queryFn: () => client.get<EngineerDto>(`/engineers/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useUpdateEngineer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { baselinePoints?: number; baselineCycleDays?: number }) =>
      client.patch<EngineerDto>(`/engineers/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: engineerKeys.detail(id) }),
  });
}

export function useEngineerSignals(id: string) {
  return useQuery({
    queryKey: engineerKeys.signals(id),
    queryFn: () => client.get<OverworkSignalsDto>(`/engineers/${id}/signals`).then((r) => r.data),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateOverride(engineerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; expiresAt?: string }) =>
      client.post<OverworkOverrideDto>(`/engineers/${engineerId}/override`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: engineerKeys.signals(engineerId) });
      qc.invalidateQueries({ queryKey: engineerKeys.overrides(engineerId) });
    },
  });
}

export function useEngineerOverrides(engineerId: string) {
  return useQuery({
    queryKey: engineerKeys.overrides(engineerId),
    queryFn: () => client.get<OverworkOverrideDto[]>(`/engineers/${engineerId}/overrides`).then((r) => r.data),
    enabled: !!engineerId,
  });
}

export function useBaselineHistory(engineerId: string) {
  return useQuery({
    queryKey: engineerKeys.baselineHistory(engineerId),
    queryFn: () =>
      client.get<BaselineHistoryEntryDto[]>(`/engineers/${engineerId}/baseline-history`).then((r) => r.data),
    enabled: !!engineerId,
  });
}
